import * as THREE from "three";

export default function(x, y, z, hero, offset, vueBus, textureLoader) {
  /*
   * @function - Arm
   * Creates arm element to add to villain
   * @param - x2 (Number): X position of arm
   * @param - y2 (Number): Y position of arm
   * @param - z2 (Number): Z position of arm
   * @param - materialStone (ThreeJS Material Instance): Material to use
   * @param - materialBluestone (ThreeJS Material Instance): Material to use
   * @param - materialGreenstone (ThreeJS Material Instance): Material to use
   * @param - materialRedstone (ThreeJS Material Instance): Material to use
   */
  function Arm(
    x2,
    y2,
    z2,
    { materialStone, materialBluestone, materialGreenstone, materialRedstone }
  ) {
    Object.setPrototypeOf(this, new THREE.Object3D());
    this.position.set(x2, y2, z2);

    const geometry = new THREE.SphereBufferGeometry(0.0975, 15);

    const mesh1 = new THREE.Mesh(geometry, materialStone);
    mesh1.position.y = 0.375;

    const mesh2 = new THREE.Mesh(geometry, materialBluestone);
    mesh2.position.y = 0.1725;

    const mesh3 = new THREE.Mesh(geometry, materialGreenstone);
    mesh3.position.y = -0.03;

    const mesh4 = new THREE.Mesh(geometry, materialRedstone);
    mesh4.position.y = -0.2325;

    this.add(mesh1);
    this.add(mesh2);
    this.add(mesh3);
    this.add(mesh4);
  }

  /*
   * @function - Korn
   * Creates korn element to add to villain
   * @param - x2 (Number): X position of korn
   * @param - y2 (Number): Y position of korn
   * @param - z2 (Number): Z position of korn
   * @param - rotx (Number): X rotation of korn
   * @param - material (ThreeJS Material Instance): Material to use
   */
  function Korn(x2, y2, z2, rotx, material) {
    Object.setPrototypeOf(this, new THREE.Object3D());
    this.position.set(x2, y2, z2);
    this.rotation.x = rotx;

    const geometry = new THREE.CylinderBufferGeometry(0, 0.15, 0.375);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = 0.8625;

    this.add(mesh);
  }

  /*
   * @function - Tail
   * Creates tail element to add to villain
   * @param - x2 (Number): X position of tail
   * @param - y2 (Number): Y position of tail
   * @param - z2 (Number): Z position of tail
   * @param - materialBluepatron (ThreeJS Material Instance): Material to use
   * @param - materialRedstone (ThreeJS Material Instance): Material to use
   * @param - materialGreenstone (ThreeJS Material Instance): Material to use
   */
  function Tail(x2, y2, z2, { materialBluepatron, materialRedstone, materialGreenstone }) {
    Object.setPrototypeOf(this, new THREE.Object3D());
    this.position.set(x2, y2, z2);
    this.rotation.y = Math.PI / 2;

    const geometryTail = new THREE.TorusBufferGeometry(0.45, 0.06, 14, 20, Math.PI * (2 / 3));
    const tail = new THREE.Mesh(geometryTail, materialBluepatron);
    tail.rotation.y = Math.PI / 2;
    tail.rotation.x = -Math.PI * (3 / 4);
    tail.position.z = -0.6;
    tail.position.y = 0.2625;

    const geometryBoundary = new THREE.SphereBufferGeometry(0.06, 15);
    const ending = new THREE.Mesh(geometryBoundary, materialRedstone);
    ending.position.z = -1.035;
    ending.position.y = 0.15;

    const beginning = new THREE.Mesh(geometryBoundary, materialGreenstone);
    beginning.position.z = -0.285;
    beginning.position.y = -0.06;

    this.add(tail);
    this.add(ending);
    this.add(beginning);
  }

  /*
   * @function - Body
   * Creates body element to add to villain
   * @param - material (ThreeJS Material Instance): Material to use
   */
  function Body(material) {
    Object.setPrototypeOf(this, new THREE.Object3D());
    this.rotation.y = Math.PI / 2;

    const geometry = new THREE.TorusBufferGeometry(0.375, 0.06, 10.5, 15);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  /*
   * @function - Eye
   * Creates eye element to add to villain
   * @param - material (ThreeJS Material Instance): Material to use
   */
  function Eye(material) {
    Object.setPrototypeOf(this, new THREE.Object3D());

    const geometry = new THREE.SphereBufferGeometry(0.3, 20);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  Object.setPrototypeOf(this, new THREE.Object3D());
  this.position.set(x, y, z);
  this.name = "Villain";

  const textureStone = textureLoader.load("/img/3DTextures/xterminate/game/stone.jpg");
  const textureBluestone = textureLoader.load("/img/3DTextures/xterminate/game/bluestone.jpg");
  const textureGreenstone = textureLoader.load("/img/3DTextures/xterminate/game/greenstone.jpg");
  const textureRedstone = textureLoader.load("/img/3DTextures/xterminate/game/redstone.jpg");
  const textureMetal = textureLoader.load("/img/3DTextures/xterminate/game/metal.jpg");
  const textureBluepatron = textureLoader.load("/img/3DTextures/xterminate/game/bluepatron.jpg");
  const textureLight = textureLoader.load("/img/3DTextures/xterminate/game/light.jpg");
  const textureEye = textureLoader.load("/img/3DTextures/xterminate/game/eye.jpg");

  const materialStone = new THREE.MeshPhongMaterial({ map: textureStone });
  const materialBluestone = new THREE.MeshPhongMaterial({ map: textureBluestone });
  const materialGreenstone = new THREE.MeshPhongMaterial({ map: textureGreenstone });
  const materialRedstone = new THREE.MeshPhongMaterial({ map: textureRedstone });
  const materialMetal = new THREE.MeshPhongMaterial({ map: textureMetal });
  const materialBluepatron = new THREE.MeshPhongMaterial({ map: textureBluepatron });
  const materialLight = new THREE.MeshPhongMaterial({ map: textureLight });
  const materialEye = new THREE.MeshPhongMaterial({ map: textureEye });

  const body = new Body(materialLight);
  const eye = new Eye(materialEye);
  const kornL = new Korn(0, -0.225, 0, -Math.PI / 7, materialMetal);
  const kornR = new Korn(0, -0.225, 0, Math.PI / 7, materialMetal);
  const armL = new Arm(0, -0.225, 0.525, {
    materialStone,
    materialBluestone,
    materialGreenstone,
    materialRedstone
  });
  const armR = new Arm(0, -0.225, -0.525, {
    materialStone,
    materialBluestone,
    materialGreenstone,
    materialRedstone
  });
  const tail = new Tail(0, -0.225, 0, { materialBluepatron, materialRedstone, materialGreenstone });

  this.add(body);
  this.add(eye);
  this.add(kornL);
  this.add(kornR);
  this.add(armL);
  this.add(armR);
  this.add(tail);

  const vector = new THREE.Vector3(0, 0, 0);
  const raycaster = new THREE.Raycaster(this.position, vector, 0, 50);
  const avance = 0.1;
  let rotMembers = 0.04;
  let rotBody = 0.05;
  let isDestroyed = false;
  let attackVal = 0.02;
  let deltaAng = 0;
  let colision = 0;

  /*
   * @function - sensing
   * Sensing of agents oriented programming 3d interfaces
   * @param - scene (ThreeJS Scene Instance): Scene instance of the 3d scene
   */
  function sensing(scene) {
    if (
      hero &&
      Math.abs(this.position.x - hero.position.x) + Math.abs(this.position.z - hero.position.z) < 20
    ) {
      const objects = scene.children.filter(el => el.name === "Hero" || el.name === "Wall");

      vector.set(
        Math.cos(this.rotation.y - Math.PI / 4 + deltaAng),
        0,
        -Math.sin(this.rotation.y - Math.PI / 4 + deltaAng)
      );
      raycaster.set(this.position, vector);
      const objectFront1 = raycaster.intersectObjects(objects);

      vector.set(
        Math.cos(this.rotation.y - Math.PI / 8 + deltaAng),
        0,
        -Math.sin(this.rotation.y - Math.PI / 8 + deltaAng)
      );
      raycaster.set(this.position, vector);
      const objectFront2 = raycaster.intersectObjects(objects);

      vector.set(Math.cos(this.rotation.y), 0, -Math.sin(this.rotation.y));
      raycaster.set(this.position, vector);
      const objectFront3 = raycaster.intersectObjects(objects);

      vector.set(
        Math.cos(this.rotation.y + Math.PI / 8 - deltaAng),
        0,
        -Math.sin(this.rotation.y + Math.PI / 8 - deltaAng)
      );
      raycaster.set(this.position, vector);
      const objectFront4 = raycaster.intersectObjects(objects);

      vector.set(
        Math.cos(this.rotation.y + Math.PI / 4 - deltaAng),
        0,
        -Math.sin(this.rotation.y + Math.PI / 4 - deltaAng)
      );
      raycaster.set(this.position, vector);
      const objectFront5 = raycaster.intersectObjects(objects);

      deltaAng += 9 / 500; // One to one degrees
      if (deltaAng >= 3839 / 10000) {
        // If 22 degrees, reset value
        deltaAng = 0;
      }

      if (
        (objectFront1.length > 0 &&
          objectFront1[0].distance >= 0.4 &&
          objectFront1[0].object.name === "Hero") ||
        (objectFront2.length > 0 &&
          objectFront2[0].distance >= 0.4 &&
          objectFront2[0].object.name === "Hero") ||
        (objectFront3.length > 0 &&
          objectFront3[0].distance >= 0.4 &&
          objectFront3[0].object.name === "Hero") ||
        (objectFront4.length > 0 &&
          objectFront4[0].distance >= 0.4 &&
          objectFront4[0].object.name === "Hero") ||
        (objectFront5.length > 0 &&
          objectFront5[0].distance >= 0.4 &&
          objectFront5[0].object.name === "Hero")
      ) {
        colision = 1;
      } else if (
        (objectFront1.length > 0 &&
          objectFront1[0].distance <= 0.9 &&
          objectFront1[0].object.name !== "Hero") ||
        (objectFront2.length > 0 &&
          objectFront2[0].distance <= 0.9 &&
          objectFront2[0].object.name !== "Hero") ||
        (objectFront3.length > 0 &&
          objectFront3[0].distance <= 0.9 &&
          objectFront3[0].object.name !== "Hero") ||
        (objectFront4.length > 0 &&
          objectFront4[0].distance <= 0.9 &&
          objectFront4[0].object.name !== "Hero") ||
        (objectFront5.length > 0 &&
          objectFront5[0].distance <= 0.9 &&
          objectFront5[0].object.name !== "Hero")
      ) {
        colision = 2;
      } else {
        colision = 0;
      }
    }
  }

  /*
   * @function - perform
   * Action of agents oriented programming 3d interfaces
   * @param - scene (ThreeJS Scene Instance): Scene instance of the 3d scene
   */
  function perform() {
    if (
      Math.abs(this.position.x - hero.position.x) + Math.abs(this.position.z - hero.position.z) <
      20
    ) {
      this.position.x += avance * Math.cos(this.rotation.y);
      this.position.z -= avance * Math.sin(this.rotation.y);

      if (Math.abs(armL.rotation.z) > 0.7) {
        rotMembers = -rotMembers;
      }

      if (Math.abs(body.rotation.x) > Math.PI * 5) {
        rotBody = -rotBody;
      }

      if (colision === 1) {
        armL.rotation.z += rotMembers;
        armR.rotation.z -= rotMembers;
        tail.rotation.x += rotMembers;
        body.rotation.x += rotBody;
        this.rotation.y = Math.atan2(
          this.position.z - hero.position.z,
          hero.position.x - this.position.x
        );

        if (
          Math.abs(hero.position.z - this.position.z) < 1 &&
          Math.abs(hero.position.x - this.position.x) < 1
        ) {
          if (isDestroyed === true) {
            vueBus.$emit("Xterminate: villain destroyed", this);
            hero.charge(-1);
          } else if (attackVal < 1) {
            attackVal += 0.02;
            hero.damaged(attackVal);
          } else if (attackVal > 1) {
            attackVal = 1;
            hero.damaged(attackVal);
          }
        }
      } else {
        armL.rotation.z = 0;
        armR.rotation.z = 0;
        tail.rotation.x = 0;
        body.rotation.x = 0.5;

        if (this.position.x > offset - 1) {
          this.rotation.y = Math.PI;
        } else if (this.position.x < 1 - offset) {
          this.rotation.y = 0;
        }

        if (this.position.z > offset - 1) {
          this.rotation.y = Math.PI / 2;
        } else if (this.position.z < 1 - offset) {
          this.rotation.y = -Math.PI / 2;
        }

        if (
          colision === 2 ||
          (body.rotation.x < 0.05 && body.rotation.x > -0.01) ||
          (body.rotation.x < 1.05 && body.rotation.x > 0.99)
        ) {
          if (Math.random() <= 0.5) {
            this.rotation.y += Math.PI / 2;
          } else {
            this.rotation.y -= Math.PI / 2;
          }
        }
      }
    }
  }

  /*
   * @function - damaged
   * Sets damage value to sets to hero if is attacked by this villain
   * @param - num (Number): Value to damage hero
   */
  function damaged(num) {
    attackVal = num;
  }

  /*
   * @function - killed
   * Kills villain
   * @param - num (Boolean): true or false
   */
  function killed(num) {
    isDestroyed = num;
  }

  this.sensing = sensing;
  this.perform = perform;
  this.damaged = damaged;
  this.killed = killed;
}
